import {
    Agency,
    ListRequestPayload,
    PasswordUpdate,
    Profile,
    UserAccess,
    WebsiteInfo,
} from "../services/account/types";
import { Country, Timezone, Language } from "../services/other/types";
import {
    addAgencyUsers,
    cacheClear,
    allCacheClear,
    checkSubdomain,
    checkUsername,
    deleteAgencyProfile,
    deleteAgencyUser,
    fetchAgencyUsages,
    getAgencyProfile,
    getAgencyUsers,
    getCurrentCountry,
    getCurrentLanguage,
    storeCurrentLanguage,
    getTranslationText,
    getUserById,
    purgeCloudflareCache,
    storeAgencyAddress,
    storeUserAgency,
    storeWebsiteInfo,
    updateAgencyProfile,
    updatePassword,
    updateProfile,
    updateSitePassword,
    toggleSitePassword,
    checkSmtpConfig
} from "../services/account";
import { getCountries, getTimezones, getLanguages } from "../services/other";

import { createToaster } from "@/toast";
import { defineStore } from "pinia";

const toast = createToaster();
export const useAccountStore = defineStore("account", {
    state: () => ({
        timezones: [] as Timezone[],
        countries: [] as Country[],
        languages: [] as Language[],
        companyProfile: {} as Agency,
        users: {
            data: [] as Profile[],
        },
        has_configured_smtp: null as boolean | null,
        smtp_verification_status: null as string | null,
        smtp_check_loaded: false,
    }),
    getters: {},
    actions: {
        async dispatchGetCountries() {
            if (!this.countries.length) {
                const response = await getCountries();
                this.countries = response.data.data;
            }
        },
        async dispatchGetLanguages() {
            if (!this.languages.length) {
                const response = await getLanguages();
                this.languages = response.data.data;
            }
        },
        async dispatchGetTimezones() {
            const response = await getTimezones();
            this.timezones = response.data.data;
        },
        async dispatchUpdateProfile(formData: Profile) {
            return await updateProfile(formData);
        },
        async dispatchCheckUsername(formData: { username: string }) {
            return await checkUsername(formData);
        },

        async dispatchUpdateAgencyProfile(formData: Agency) {
            return await updateAgencyProfile(formData);
        },
        async dispatchGetAgencyProfile() {
            try {
                const response = await getAgencyProfile();
                this.companyProfile = response.data.data;
            } catch (error) {
                // toast.error(error.response.data.message);
            }
        },
        async dispatchChangePassword(formData: PasswordUpdate) {
            return await updatePassword(formData);
        },
        async dispatchChangeSitePassword(formData) {
            return await updateSitePassword(formData);
        },

        async dispatchDeleteAgencyProfile(formData: { security_key: string }) {
            return await deleteAgencyProfile(formData);
        },

        async dispatchGetAgencyUsers(payload: ListRequestPayload) {
            const response = await getAgencyUsers(payload);
            this.users = response.data.data;
            return response;
        },

        async dispatchAddAgencyUsers(formData) {
            return await addAgencyUsers(formData);
        },

        async getUserById(userId) {
            const response = await getUserById(userId);
            return response.data.data;
        },
        async dispatchDeleteAgencyUser(userId) {
            return await deleteAgencyUser(userId);
        },
        async dispatchAgencyUsages() {
            return await fetchAgencyUsages();
        },
        async purgeCloudflareCache() {
            return await purgeCloudflareCache();
        },
        async cacheClear() {
            return await cacheClear();
        },
        async allCacheClear() {
            return await allCacheClear();
        },
        async dispatchCheckSubdomainAvailability(payload) {
            return await checkSubdomain(payload);
        },

        async dispatchStoreWebsiteInfo(payload) {
            return await storeWebsiteInfo(payload);
        },

        updateWebsiteInfo(websiteInfo: WebsiteInfo) {
            if (this.companyProfile && this.companyProfile.id) {
                this.companyProfile.website_info = { ...websiteInfo };
            }
        },

        async dispatchStoreAgencyAddress(payload) {
            return await storeAgencyAddress(payload);
        },

        async dispatchSaveAgency(formData) {
            return await storeUserAgency(formData);
        },
        async dispatchGetCurrentCountry(ip = null) {
            return await getCurrentCountry(ip);
        },
        async dispatchGetCurrentLanguage() {
            return await getCurrentLanguage();
        },
        async dispatchStoreCurrentLanguage(formData) {
            return await storeCurrentLanguage(formData);
        },
        async dispatchGetTranslationText(translated_page, untranslated_page, filter) {
            return await getTranslationText(translated_page, untranslated_page, filter);
        },
        async dispatchStoreTranslation(formData) {
            return await storeTranslation(formData);
        },
        async dispatchToggleSitePassword(formData) {
            return await toggleSitePassword(formData);
        },
        async dispatchCheckSmtpConfig() {
            const response = await checkSmtpConfig();
            this.has_configured_smtp = !!response.data.data.integration;
            this.smtp_verification_status = response.data.data.smtp_verification_status ?? null;
            this.smtp_check_loaded = true;
            return response;
        },
    },
});
